import { Injectable } from '@angular/core';
import { Observable, of, map, forkJoin, switchMap, catchError } from 'rxjs';
import { HttpService } from './http.service';
import { MapboxLayerConfig } from '../interfaces/mapbox-layer.interface';
import { PermissionDefinition, Role } from '../interfaces/role.interface';
import {
  City,
  CropAccess,
  CropDefinition,
  CropYearLocation,
  District,
  EnterpriseConfig,
  Hub,
  LocationHierarchy,
  Region,
  SeasonDefinition,
  State,
  YearLocation,
} from '../interfaces/config.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  seasonDefinitions: SeasonDefinition[] = [];
  cropDefinitions: CropDefinition[] = [];
  permissionDefinitions: PermissionDefinition[] = [];

  enterpriseConfig: EnterpriseConfig;
  userRole: {
    Role: Role;
    GeoAccesses: CropAccess[];
  };

  enterpriseCropYearLocations: CropYearLocation[] = [];
  enterpriseCountries: LocationHierarchy[] = [];
  // enterpriseStates: State[] = [];
  enterpriseRegions: Region[] = [];
  enterpriseHub: Hub[] = [];
  enterpriseCities: City[] = [];
  enterpriseDistricts: District[] = [];

  authorizedCropYearLocations: CropYearLocation[] = [];
  authorizedYearLocations: YearLocation[] = [];
  authorizedCountries: LocationHierarchy[] = [];
  // authorizedStates: State[] = [];
  // authorizedHub: Hub[] = [];
  authorizedRegions: Region[] = [];
  authorizedCities: City[] = [];
  authorizedDistricts: District[] = [];

  mapboxLayerConfigs: MapboxLayerConfig[] = [];

  constructor(private httpService: HttpService) {}

  /**
   * Retrieves and processes enterprise and role configurations based on the provided enterprise and role IDs.
   *
   * This method performs the following steps:
   * - Validates the input parameters (`enterpriseId` and `roleId`).
   * - Checks if the enterprise configuration and user role are already available.
   * - Firstly, fetches season, crop, and permission definitions if not already loaded.
   * - Makes HTTP calls to retrieve enterprise configuration and role details.
   * - Processes the retrieved data to populate enterprise and authorized locations.
   * - Compares enterprise and authorized locations for consistency.
   *
   * @param enterpriseId - The ID of the enterprise to retrieve configuration for.
   * @param roleId - The ID of the role to retrieve configuration for.
   * @returns An observable that emits `true` if the configurations are successfully retrieved and processed, or `false` otherwise.
   */
  getEnterpriseAndRole(enterpriseId: number, roleId: number): Observable<boolean> {
    if (!enterpriseId || !roleId) {
      return of(false);
    }
    if (this.enterpriseConfig && this.userRole) {
      return of(true);
    }
    return forkJoin([this.getSeasonDefinitions(), this.getCropDefinitions(), this.getPermissionDefinitions()]).pipe(
      switchMap(([seasonResponse, cropResponse, permissionResponse]) => {
        if (!seasonResponse || !cropResponse || !permissionResponse) {
          return of(false);
        }

        return forkJoin([
          this.httpService.serviceCall('get', environment.baseApiUrlV2 + `cropmapv2/get/config/${enterpriseId}?timeStamp=${this.timeStamp}`),
          this.httpService.serviceCall('get', environment.baseApiUrlV2 + `cropmapv2/get/role/${roleId}?timeStamp=${this.timeStamp}`),
        ]).pipe(
          map(([enterpriseConfigRes, roleResponse]) => {
            if (!(enterpriseConfigRes?.Data?.EnterpriseAccesses?.length > 0) || !(roleResponse?.Data?.GeoAccesses?.length > 0)) {
              return false;
            }

            this.enterpriseConfig = enterpriseConfigRes.Data;

            // Process enterprise accesses to populate crop year locations and hierarchical location data
            for (const item of this.enterpriseConfig.EnterpriseAccesses) {
              if (item.LocationHierarchy?.length > 0) {
                // Populate enterprise crop year locations
                this.enterpriseCropYearLocations = this.getCropYearLocation(item, this.enterpriseCropYearLocations);

                // Initialize hierarchical location data
                let locations = {
                  countries: this.enterpriseCountries,
                  regions: this.enterpriseRegions,
                  cities: this.enterpriseCities,
                  districts: this.enterpriseDistricts,
                };

                // Populate hierarchical location data
                locations = this.getLocations(item, locations);
                this.enterpriseCountries = locations.countries;
                this.enterpriseRegions = locations.regions;
                this.enterpriseCities = locations.cities;
                this.enterpriseDistricts = locations.districts;
              }
            }

            console.log('this.enterpriseCropYearLocations :>> ', this.enterpriseCropYearLocations);

            this.userRole = {
              Role: roleResponse.Data.Role as Role,
              GeoAccesses: roleResponse.Data.GeoAccesses as any,
            };

            // If all GeoAccesses have empty LocationHierarchy, return false as no valid access is defined
            if (this.userRole.GeoAccesses.every((e) => e.LocationHierarchy.length === 0)) {
              return false;
            }

            for (const item of this.userRole.GeoAccesses) {
              if (item.LocationHierarchy?.length > 0) {
                this.authorizedCropYearLocations = this.getCropYearLocation(item, this.authorizedCropYearLocations);

                let locations = {
                  countries: this.authorizedCountries,
                  regions: this.enterpriseRegions,
                  cities: this.authorizedCities,
                  districts: this.authorizedDistricts,
                };
                locations = this.getLocations(item, locations);
                this.authorizedCountries = locations.countries;
                this.authorizedRegions = locations.regions;
                this.authorizedCities = locations.cities;
                this.authorizedDistricts = locations.districts;
              }
            }

            this.compareEnterpriseAndAuthorizedLocations();
            console.log('this.authorizedCropYearLocations :>> ', this.authorizedCropYearLocations);

            return true;
          }),
          catchError((error) => {
            console.log('error :>> ', error);
            return of(false);
          }),
        );
      }),
      catchError(() => of(false)),
    );
  }

  /**
   * Processes and organizes crop year location data by updating or adding hierarchical location information
   * for a given crop access item. This function ensures that the data structure is updated with the appropriate
   * crop, year, and location hierarchy details, including countries, regions, cities, districts, and states.
   *
   * @param item - The crop access item containing crop, year, season, and location hierarchy information.
   * @param cropYearLocations - An optional array of existing crop year locations to update. Defaults to an empty array.
   * @returns An updated array of crop year locations with the hierarchical location data added or modified.
   */
  private getCropYearLocation(item: CropAccess, cropYearLocations: CropYearLocation[] = []): CropYearLocation[] {
    const cropDefinition = this.cropDefinitions.find((e) => e.Id == item.CropId);
    if (!cropDefinition) {
      return cropYearLocations;
    }
    const cropIndex = cropYearLocations.findIndex((e) => e.Crop.Id == item.CropId);
    if (cropIndex == -1) {
      cropYearLocations.push({
        Crop: cropDefinition,
        Season: item.Season,
        YearLocations: [
          {
            Year: item.Year,
            Locations: item.LocationHierarchy ?? [],
          },
        ],
      });
    } else {
      const yearIndex = cropYearLocations[cropIndex].YearLocations.findIndex((e) => e.Year == item.Year);
      if (yearIndex == -1) {
        cropYearLocations[cropIndex].YearLocations.push({
          Year: item.Year,
          Locations: item.LocationHierarchy ?? [],
        });
      } else {
        for (const country of item.LocationHierarchy ?? []) {
          const locationIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations.findIndex((e) => e.CountryId == country.CountryId);
          if (locationIndex == -1) {
            cropYearLocations[cropIndex].YearLocations[yearIndex].Locations.push(country);
          } else {
            if (country.Regions?.length > 0) {
              for (const region of country.Regions) {
                const regionIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions.findIndex(
                  (e) => e.RegionId == region.RegionId,
                );
                if (regionIndex == -1) {
                  cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions.push(region);
                } else {
                  if (region.Cities?.length > 0) {
                    for (const city of region.Cities) {
                      const cityIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions[
                        regionIndex
                      ].Cities.findIndex((e) => e.CityId == city.CityId);
                      if (cityIndex == -1) {
                        cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions[regionIndex].Cities.push(city);
                      } else {
                        if (city.Districts?.length > 0) {
                          for (const district of city.Districts) {
                            const districtIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions[
                              regionIndex
                            ].Cities[cityIndex].Districts.findIndex((e) => e.DistrictId == district.DistrictId);
                            if (districtIndex == -1) {
                              cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].Regions[regionIndex].Cities[
                                cityIndex
                              ].Districts.push(district);
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            } else if (country.States?.length > 0) {
              for (const state of country.States) {
                const stateIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States.findIndex(
                  (e) => e.StateId == state.StateId,
                );
                if (stateIndex == -1) {
                  cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States.push(state);
                } else {
                  if (state.Cities?.length > 0) {
                    for (const city of state.Cities) {
                      const cityIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States[
                        stateIndex
                      ].Cities.findIndex((e) => e.CityId == city.CityId);
                      if (cityIndex == -1) {
                        cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States[stateIndex].Cities.push(city);
                      } else {
                        if (city.Districts?.length > 0) {
                          for (const district of city.Districts) {
                            const districtIndex = cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States[
                              stateIndex
                            ].Cities[cityIndex].Districts.findIndex((e) => e.DistrictId == district.DistrictId);
                            if (districtIndex == -1) {
                              cropYearLocations[cropIndex].YearLocations[yearIndex].Locations[locationIndex].States[stateIndex].Cities[
                                cityIndex
                              ].Districts.push(district);
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return cropYearLocations;
  }

  getAuthorizedYearLocations(): YearLocation[] {
    const yearLocations: YearLocation[] = [];

    for (const crop of this.authorizedCropYearLocations) {
      for (const yearLocation of crop.YearLocations) {
        if (!yearLocations.find((e) => e.Year == yearLocation.Year)) {
          yearLocations.push(yearLocation);
        }
      }
    }

    return yearLocations.sort((a, b) => b.Year - a.Year);
  }

  getSeasons(type: 'enterprise' | 'authorized' = 'authorized'): SeasonDefinition[] {
    const seasons: SeasonDefinition[] = [];

    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    for (const item of cropYearLocations) {
      if (!seasons.find((e) => e.Id == item.Season.Id)) {
        seasons.push(item.Season);
      }
    }
    // put winter to the first
    return seasons.sort((a, b) => b.Id - a.Id);
  }

  getCropsBySeasonId(seasonIds: number[], type: 'enterprise' | 'authorized' = 'authorized'): CropDefinition[] {
    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    const cropIds = cropYearLocations.filter((e) => seasonIds.includes(e.Season.Id)).map((e) => e.Crop.Id);
    return this.cropDefinitions.filter((e) => cropIds.includes(e.Id));
  }

  getCropsByYear(seasonIds: number[], year: number, type: 'enterprise' | 'authorized' = 'authorized'): CropDefinition[] {
    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    const cropIds = cropYearLocations
      .filter((e) => seasonIds.includes(e.Season.Id))
      .filter((e) => e.YearLocations.some((f) => f.Year == year))
      .map((e) => e.Crop.Id);
    return this.cropDefinitions.filter((e) => cropIds.includes(e.Id));
  }

  getCropsByYearCountry(seasonIds: number[], year: number, countryId: number, type: 'enterprise' | 'authorized' = 'authorized'): CropDefinition[] {
    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    const cropIds = cropYearLocations
      .filter((e) => seasonIds.includes(e.Season.Id))
      .filter((e) => e.YearLocations.some((f) => f.Year == year && f.Locations.some((g) => g.CountryId == countryId)))
      .map((e) => e.Crop.Id);
    return this.cropDefinitions.filter((e) => cropIds.includes(e.Id));
  }

  getYearsByCropIds(cropIds: number[], type: 'enterprise' | 'authorized' = 'authorized'): YearLocation[] {
    const years: YearLocation[] = [];

    let cropYearLocations = type == 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;

    // Filter only the relevant cropYearLocations
    cropYearLocations = cropYearLocations.filter((e) => cropIds.includes(e.Crop.Id));

    for (const cropYearLocation of cropYearLocations) {
      for (const yearLocation of cropYearLocation.YearLocations) {
        let yearObj = years.find((e) => e.Year === yearLocation.Year);

        if (!yearObj) {
          // If the year does not exist, add it
          yearObj = { Year: yearLocation.Year, Locations: [] };
          years.push(yearObj);
        }

        for (const location of yearLocation.Locations) {
          let existingLocation = yearObj.Locations.find((loc) => loc.CountryId === location.CountryId);

          if (!existingLocation) {
            // If location does not exist, add it
            yearObj.Locations.push({ ...location, Regions: [], States: [] });
            existingLocation = yearObj.Locations[yearObj.Locations.length - 1];
          }

          // Add Regions uniquely
          for (const region of location.Regions || []) {
            if (!existingLocation.Regions.some((r) => r.RegionId === region.RegionId)) {
              existingLocation.Regions.push({ ...region, Cities: [] });
            }

            const existingRegion = existingLocation.Regions.find((r) => r.RegionId === region.RegionId);
            if (existingRegion) {
              for (const city of region.Cities || []) {
                if (!existingRegion.Cities.some((c) => c.CityId === city.CityId)) {
                  existingRegion.Cities.push(city);
                }
              }
            }
          }

          // Add States uniquely
          for (const state of location.States || []) {
            if (!existingLocation.States.some((s) => s.StateId === state.StateId)) {
              existingLocation.States.push({ ...state, Cities: [] });
            }

            const existingState = existingLocation.States.find((s) => s.StateId === state.StateId);
            if (existingState) {
              for (const city of state.Cities || []) {
                if (!existingState.Cities.some((c) => c.CityId === city.CityId)) {
                  existingState.Cities.push(city);
                }
              }
            }
          }
        }
      }
    }

    return years.sort((a, b) => b.Year - a.Year);
  }

  getCountriesByYear(yearLocations: YearLocation[]): LocationHierarchy[] {
    const coutries: LocationHierarchy[] = [];
    for (const yearLocation of yearLocations) {
      for (const country of yearLocation.Locations) {
        const locationIndex = coutries.findIndex((e) => e.CountryId == country.CountryId);
        if (locationIndex === -1) {
          coutries.push(country);
        } else {
          if (country.Regions?.length > 0) {
            for (const region of country.Regions) {
              const regionIndex = coutries[locationIndex].Regions.findIndex((e) => e.RegionId == region.RegionId);
              if (regionIndex == -1) {
                coutries[locationIndex].Regions.push(region);
              } else {
                if (region.Cities?.length > 0) {
                  for (const city of region.Cities) {
                    const cityIndex = coutries[locationIndex].Regions[regionIndex].Cities.findIndex((e) => e.CityId == city.CityId);
                    if (cityIndex == -1) {
                      coutries[locationIndex].Regions[regionIndex].Cities.push(city);
                    } else {
                      if (city.Districts?.length > 0) {
                        for (const district of city.Districts) {
                          const districtIndex = coutries[locationIndex].Regions[regionIndex].Cities[cityIndex].Districts.findIndex(
                            (e) => e.DistrictId == district.DistrictId,
                          );
                          if (districtIndex == -1) {
                            coutries[locationIndex].Regions[regionIndex].Cities[cityIndex].Districts.push(district);
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          } else if (country.States?.length > 0) {
            for (const state of country.States) {
              const stateIndex = coutries[locationIndex].States.findIndex((e) => e.StateId == state.StateId);
              if (stateIndex == -1) {
                coutries[locationIndex].States.push(state);
              } else {
                if (state.Cities?.length > 0) {
                  for (const city of state.Cities) {
                    const cityIndex = coutries[locationIndex].States[stateIndex].Cities.findIndex((e) => e.CityId == city.CityId);
                    if (cityIndex == -1) {
                      coutries[locationIndex].States[stateIndex].Cities.push(city);
                    } else {
                      if (city.Districts?.length > 0) {
                        for (const district of city.Districts) {
                          const districtIndex = coutries[locationIndex].States[stateIndex].Cities[cityIndex].Districts.findIndex(
                            (e) => e.DistrictId == district.DistrictId,
                          );
                          if (districtIndex == -1) {
                            coutries[locationIndex].States[stateIndex].Cities[cityIndex].Districts.push(district);
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return coutries;
  }

  getCitiesByCountry(country: LocationHierarchy): City[] {
    const cities: City[] = [];
    for (const region of country.Regions ?? []) {
      for (const city of region.Cities ?? []) {
        if (!cities.find((e) => e.CityId == city.CityId)) {
          cities.push(city);
        }
      }
    }
    for (const states of country.States ?? []) {
      for (const city of states.Cities ?? []) {
        if (!cities.find((e) => e.CityId == city.CityId)) {
          cities.push(city);
        }
      }
    }
    return cities;
  }

  getCitiesByCountryId(countryId: number): City[] {
    const cities: City[] = [];
    for (const city of this.authorizedCities.filter(e => e.CountryId == countryId) ?? []) {
      if (!cities.find((e) => e.CityId == city.CityId)) {
        cities.push(city);
      }
    }
    return cities;
  }

  getSeasonsByCountryId(countryId: number, type: 'enterprise' | 'authorized' = 'authorized'): SeasonDefinition[] {
    const seasons: SeasonDefinition[] = [];

    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    for (const item of cropYearLocations) {
      for (const yearLocation of item.YearLocations) {
        for (const location of yearLocation.Locations) {
          if (location.CountryId === countryId) {
            if (!seasons.find((e) => e.Id == item.Season.Id)) {
              seasons.push(item.Season);
            }
          }
        }
      }
    }

    return seasons;
  }

  getYearsByCountryId(countryId: number, type: 'enterprise' | 'authorized' = 'authorized'): YearLocation[] {
    const years: YearLocation[] = [];

    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    for (const item of cropYearLocations) {
      for (const yearLocation of item.YearLocations) {
        for (const location of yearLocation.Locations) {
          if (location.CountryId === countryId) {
            if (!years.find((e) => e.Year == yearLocation.Year)) {
              years.push(yearLocation);
            }
          }
        }
      }
    }

    return years.sort((a, b) => a.Year - b.Year);
  }

  getCropsByCountryId(countryId: number, type: 'enterprise' | 'authorized' = 'authorized'): CropDefinition[] {
    const cropSet = new Set<CropDefinition>();

    let cropYearLocations = this.authorizedCropYearLocations;
    if (type == 'enterprise') {
      cropYearLocations = this.enterpriseCropYearLocations;
    }

    for (const item of cropYearLocations) {
      for (const yearLocation of item.YearLocations) {
        for (const location of yearLocation.Locations) {
          if (location.CountryId === countryId) {
            cropSet.add(item.Crop);
          }
        }
      }
    }

    return Array.from(cropSet);
  }

  /* -------------------------------------------------------------------------- */
  /* -------------------------------------------------------------------------- */
  private getLocations(
    item: CropAccess,
    locations: { countries: LocationHierarchy[]; regions: Region[]; cities: City[]; districts: District[] },
  ): { countries: LocationHierarchy[]; regions: Region[]; cities: City[]; districts: District[] } {
    for (const countryItem of item.LocationHierarchy) {
      const country = locations.countries.find((e) => e.CountryId == countryItem.CountryId);
      if (!country) {
        locations.countries.push({
          CountryId: countryItem.CountryId,
          Name: countryItem.Name,
          // Hubs: [],
          // States: [],
          Regions: [],
        });
      }

      if (countryItem.Regions?.length > 0) {
        for (const regionItem of countryItem.Regions) {
          const region = locations.regions.find((e) => e.RegionId == regionItem.RegionId);
          if (!region) {
            locations.regions.push({
              RegionId: regionItem.RegionId,
              Name: regionItem.Name,
              Cities: [],
              CountryId: countryItem.CountryId,
            });
          }

          for (const cityItem of regionItem.Cities) {
            const city = locations.cities.find((e) => e.CityId == cityItem.CityId);
            if (!city) {
              locations.cities.push({
                CityId: cityItem.CityId,
                Name: cityItem.Name,
                Districts: [],
                RegionId: regionItem.RegionId,
                CountryId: countryItem.CountryId,
              });
            }
            for (const districtItem of cityItem.Districts) {
              const city = locations.districts.find((e) => e.DistrictId == districtItem.DistrictId);
              if (!city) {
                locations.districts.push({
                  DistrictId: districtItem.DistrictId,
                  Name: districtItem.Name,
                  Villages: [],
                  CityId: cityItem.CityId,
                });
              }
            }
          }
        }
      } else {
        for (const stateItem of countryItem.States) {
          const state = locations.regions.find((e) => e.RegionId == stateItem.StateId);
          if (!state) {
            locations.regions.push({
              RegionId: stateItem.StateId,
              Name: stateItem.Name,
              Cities: [],
              CountryId: countryItem.CountryId,
            });
          }

          for (const cityItem of stateItem.Cities) {
            const city = locations.cities.find((e) => e.CityId == cityItem.CityId);
            if (!city) {
              locations.cities.push({
                CityId: cityItem.CityId,
                Name: cityItem.Name,
                Districts: [],
                RegionId: stateItem.StateId,
                CountryId: countryItem.CountryId,
              });
            }
            for (const districtItem of cityItem.Districts) {
              const city = locations.districts.find((e) => e.DistrictId == districtItem.DistrictId);
              if (!city) {
                locations.districts.push({
                  DistrictId: districtItem.DistrictId,
                  Name: districtItem.Name,
                  Villages: [],
                  CityId: cityItem.CityId,
                });
              }
            }
          }
        }
      }
    }
    return locations;
  }

  getSeasonDefinitions(): Observable<any> {
    return this.httpService.serviceCall('get', environment.baseApiUrlV2 + `constants/list/by/groupId?groupId=1709&timeStamp=${this.timeStamp}`).pipe(
      map((res) => {
        if (res.Data?.length > 0) {
          this.seasonDefinitions = res.Data.map((e: any) => ({ Id: e.ConstantId, Name: e.ConstantValue }));
          return true;
        }
        return false;
      }),
    );
  }

  getCropDefinitions(): Observable<any> {
    return this.httpService.serviceCall('get', environment.baseApiUrlV2 + `cropmapv2/list/crops?timeStamp=${this.timeStamp}`).pipe(
      map((res) => {
        if (res.Data?.length > 0) {
          this.cropDefinitions = res.Data ?? [];
          return true;
        }
        return false;
      }),
    );
  }

  getPermissionDefinitions(): Observable<any> {
    return this.httpService.serviceCall('get', environment.baseApiUrlV2 + `cropmapv2/list/permissions?timeStamp=${this.timeStamp}`).pipe(
      map((res) => {
        if (res.Data?.length > 0) {
          this.permissionDefinitions = res.Data ?? [];
          return true;
        }
        return false;
      }),
    );
  }

  getMapboxLayerConfigs(): Observable<any> {
    if (this.mapboxLayerConfigs?.length > 0) {
      return of(this.mapboxLayerConfigs);
    }
    // this.mapboxLayerConfigs = layers;
    // return of(this.mapboxLayerConfigs);

    return this.httpService.serviceCall('get', environment.baseApiUrlV2 + `cropmaplayer/get/layers?timeStamp=${this.timeStamp}`).pipe(
      map((res) => {
        this.mapboxLayerConfigs = res?.Data ?? [];
        return this.mapboxLayerConfigs;
      }),
    );
  }

  /**
   * Compares and filters the authorized crop year locations and hierarchical location data
   * to ensure they align with the enterprise crop year locations and hierarchical data.
   *
   * This method performs the following steps:
   * - Filters out any crop year locations, years, or hierarchical locations (countries, regions, cities, districts, villages)
   *   from the authorized data that do not exist in the enterprise data.
   * - Updates the authorized hierarchical location data (countries, regions, cities, districts) to match the enterprise data.
   *
   * This checking should be done on Backend side first, but as a fallback, we are doing it here.
   */
  compareEnterpriseAndAuthorizedLocations(): void {
    this.authorizedCropYearLocations = this.authorizedCropYearLocations.filter((authorizedItem) => {
      const enterpriseItem = this.enterpriseCropYearLocations.find((e) => e.Crop.Id === authorizedItem.Crop.Id);

      if (!enterpriseItem) {
        return false; // Remove the crop-year if it doesn't exist in enterprise
      }

      authorizedItem.YearLocations = authorizedItem.YearLocations.filter((authorizedYearLocation) => {
        const enterpriseYearLocation = enterpriseItem.YearLocations.find((e) => e.Year === authorizedYearLocation.Year);

        if (!enterpriseYearLocation) {
          return false; // Remove the year location if it doesn't exist in enterprise
        }

        authorizedYearLocation.Locations = authorizedYearLocation.Locations.filter((authorizedLocation) => {
          const enterpriseLocation = enterpriseYearLocation.Locations.find((e) => e.CountryId === authorizedLocation.CountryId);

          if (!enterpriseLocation) {
            return false; // Remove the location if it doesn't exist in enterprise
          }

          if (authorizedLocation.Regions?.length > 0) {
            authorizedLocation.Regions = authorizedLocation.Regions.filter((authorizedRegion) => {
              const enterpriseRegion = enterpriseLocation.Regions.find((e) => e.RegionId === authorizedRegion.RegionId);

              if (!enterpriseRegion) {
                return false; // Remove the region if it doesn't exist in enterprise
              }

              if (authorizedRegion.Cities?.length > 0) {
                authorizedRegion.Cities = authorizedRegion.Cities.filter((authorizedCity) => {
                  const enterpriseCity = enterpriseRegion.Cities.find((e) => e.CityId === authorizedCity.CityId);

                  if (!enterpriseCity) {
                    return false; // Remove the city if it doesn't exist in enterprise
                  }

                  if (authorizedCity.Districts?.length > 0) {
                    authorizedCity.Districts = authorizedCity.Districts.filter((authorizedDistrict) => {
                      const enterpriseDistrict = enterpriseCity.Districts.find((e) => e.DistrictId === authorizedDistrict.DistrictId);

                      if (!enterpriseDistrict) {
                        return false; // Remove the district if it doesn't exist in enterprise
                      }

                      if (authorizedDistrict.Villages?.length > 0) {
                        authorizedDistrict.Villages = authorizedDistrict.Villages.filter((authorizedVillage) => {
                          return enterpriseDistrict.Villages.some((e) => e.VillageId === authorizedVillage.VillageId);
                        });
                      }
                      return true;
                    });
                  }
                  return true;
                });
              }
              return true;
            });
          }
          return true;
        });

        return authorizedYearLocation.Locations.length > 0; // Keep only valid locations
      });

      return authorizedItem.YearLocations.length > 0; // Keep only valid year locations
    });

    const authorizedCountries = structuredClone(this.authorizedCountries);
    const authorizedRegions = structuredClone(this.authorizedRegions);
    const authorizedCities = structuredClone(this.authorizedCities);
    const authorizedDistricts = structuredClone(this.authorizedDistricts);

    this.authorizedCountries = authorizedCountries.filter((authorizedCountry) => {
      const enterpriseCountry = this.enterpriseCountries.find((e) => e.CountryId === authorizedCountry.CountryId);

      if (!enterpriseCountry) {
        return false; // Remove the country if it doesn't exist in enterprise
      }

      if (authorizedCountry.Regions?.length > 0) {
        authorizedCountry.Regions = authorizedCountry.Regions.filter((authorizedRegion) => {
          const enterpriseRegion = enterpriseCountry.Regions.find((e) => e.RegionId === authorizedRegion.RegionId);

          if (!enterpriseRegion) {
            return false; // Remove the region if it doesn't exist in enterprise
          }

          if (authorizedRegion.Cities?.length > 0) {
            authorizedRegion.Cities = authorizedRegion.Cities.filter((authorizedCity) => {
              const enterpriseCity = enterpriseRegion.Cities.find((e) => e.CityId === authorizedCity.CityId);

              if (!enterpriseCity) {
                return false; // Remove the city if it doesn't exist in enterprise
              }

              if (authorizedCity.Districts?.length > 0) {
                authorizedCity.Districts = authorizedCity.Districts.filter((authorizedDistrict) => {
                  const enterpriseDistrict = enterpriseCity.Districts.find((e) => e.DistrictId === authorizedDistrict.DistrictId);

                  if (!enterpriseDistrict) {
                    return false; // Remove the district if it doesn't exist in enterprise
                  }

                  if (authorizedDistrict.Villages?.length > 0) {
                    authorizedDistrict.Villages = authorizedDistrict.Villages.filter((authorizedVillage) => {
                      return enterpriseDistrict.Villages.some((e) => e.VillageId === authorizedVillage.VillageId);
                    });
                  }
                  return true;
                });
              }
              return true;
            });
          }
          return true;
        });
      }
      return true;
    });

    this.authorizedRegions = authorizedRegions.filter((authorizedRegion) => {
      const enterpriseRegion = this.enterpriseRegions.find((e) => e.RegionId === authorizedRegion.RegionId);

      if (!enterpriseRegion) {
        return false; // Remove the region if it doesn't exist in enterprise
      }

      if (authorizedRegion.Cities?.length > 0) {
        authorizedRegion.Cities = authorizedRegion.Cities.filter((authorizedCity) => {
          const enterpriseCity = enterpriseRegion.Cities.find((e) => e.CityId === authorizedCity.CityId);

          if (!enterpriseCity) {
            return false; // Remove the city if it doesn't exist in enterprise
          }

          if (authorizedCity.Districts?.length > 0) {
            authorizedCity.Districts = authorizedCity.Districts.filter((authorizedDistrict) => {
              const enterpriseDistrict = enterpriseCity.Districts.find((e) => e.DistrictId === authorizedDistrict.DistrictId);

              if (!enterpriseDistrict) {
                return false; // Remove the district if it doesn't exist in enterprise
              }

              if (authorizedDistrict.Villages?.length > 0) {
                authorizedDistrict.Villages = authorizedDistrict.Villages.filter((authorizedVillage) => {
                  return enterpriseDistrict.Villages.some((e) => e.VillageId === authorizedVillage.VillageId);
                });
              }
              return true;
            });
          }
          return true;
        });
      }
      return true;
    });

    this.authorizedCities = authorizedCities.filter((authorizedCity) => {
      const enterpriseCity = this.enterpriseCities.find((e) => e.CityId === authorizedCity.CityId);

      if (!enterpriseCity) {
        return false; // Remove the city if it doesn't exist in enterprise
      }

      if (authorizedCity.Districts?.length > 0) {
        authorizedCity.Districts = authorizedCity.Districts.filter((authorizedDistrict) => {
          const enterpriseDistrict = enterpriseCity.Districts.find((e) => e.DistrictId === authorizedDistrict.DistrictId);

          if (!enterpriseDistrict) {
            return false; // Remove the district if it doesn't exist in enterprise
          }

          if (authorizedDistrict.Villages?.length > 0) {
            authorizedDistrict.Villages = authorizedDistrict.Villages.filter((authorizedVillage) => {
              return enterpriseDistrict.Villages.some((e) => e.VillageId === authorizedVillage.VillageId);
            });
          }
          return true;
        });
      }
      return true;
    });

    this.authorizedDistricts = authorizedDistricts.filter((authorizedDistrict) => {
      const enterpriseDistrict = this.enterpriseDistricts.find((e) => e.DistrictId === authorizedDistrict.DistrictId);

      if (!enterpriseDistrict) {
        return false; // Remove the district if it doesn't exist in enterprise
      }

      if (authorizedDistrict.Villages?.length > 0) {
        authorizedDistrict.Villages = authorizedDistrict.Villages.filter((authorizedVillage) => {
          return enterpriseDistrict.Villages.some((e) => e.VillageId === authorizedVillage.VillageId);
        });
      }
      return true;
    });
  }

  get timeStamp() {
    return new Date().getTime();
  }

  resetAll() {
    this.enterpriseConfig = null;
    this.userRole = null;

    this.enterpriseCropYearLocations = [];
    this.enterpriseCountries = [];
    this.enterpriseRegions = [];
    this.enterpriseHub = [];
    this.enterpriseCities = [];
    this.enterpriseDistricts = [];

    this.authorizedCropYearLocations = [];
    this.authorizedCountries = [];
    this.authorizedRegions = [];
    this.authorizedCities = [];
    this.authorizedDistricts = [];

    this.mapboxLayerConfigs = [];
  }
}
